import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ContractorService, ProjectContractEntryInput, ProjectContract, LegalEntity } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { DropdownList } from '@/components/ui/DropdownList/DropdownList';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { PriceFixCombo } from '@/components/ui/PriceFixCombo/PriceFixCombo';
import { formatPriceInputDisplay } from '@/utils/formatNumber';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import { ComboEmptyCreatePanel } from '@/components/ui/ComboEmptyCreatePanel/ComboEmptyCreatePanel';
import './ContractorServiceList.css';

export interface ContractorEntryItem {
  key: string;
  /** PK строки project_contracts; null для несохранённой строки */
  project_contractor_id?: string | null;
  contractor_id: string;
  contractor_name: string;
  service_id: string | null;
  service_name: string | null;
  service_price: number | null;
  price_input: string;
  is_price_fixed: boolean;
  /** Учитывать строку в бюджете проекта */
  is_contractor_included_in_budget: boolean;
}

export function projectContractorsToEntries(contractors: ProjectContract[]): ContractorEntryItem[] {
  return contractors.map((c) => {
    const price = c.price ?? c.service_custom_price;
    const catalog = c.catalog_price ?? c.service_price ?? null;
    return {
      key: c.project_contract_id ?? Math.random().toString(36).slice(2),
      project_contractor_id: c.project_contract_id ?? null,
      contractor_id: c.legal_entity_id ?? c.contractor_id ?? c.id,
      contractor_name: contractorDisplayName({
        legal_entity: typeof c.legal_entity === 'string' ? c.legal_entity : null,
        name: c.name,
      }),
      service_id: c.contract_id ?? c.service_id ?? null,
      service_name: c.contract_name ?? c.service_name ?? null,
      service_price: catalog,
      price_input: price != null ? formatPriceInputDisplay(price) : '',
      is_price_fixed: c.is_price_fixed ?? c.is_service_price_fixed ?? false,
      is_contractor_included_in_budget:
        c.is_included_in_budget === true || c.is_contractor_included_in_budget === true,
    };
  });
}

export function entriesToInputs(items: ContractorEntryItem[]): ProjectContractEntryInput[] {
  return items
    .filter((item) => item.contractor_id)
    .map((item) => ({
      project_contract_id: item.project_contractor_id ?? null,
      legal_entity_id: item.contractor_id,
      contract_id: item.service_id,
      name: item.service_name?.trim() || 'Договор',
      price: item.price_input ? parseFloat(item.price_input) : null,
      is_price_fixed: item.is_price_fixed,
      is_included_in_budget: item.is_contractor_included_in_budget,
    }));
}

function makeEmptyEntry(): ContractorEntryItem {
  return {
    key: Math.random().toString(36).slice(2),
    project_contractor_id: null,
    contractor_id: '',
    contractor_name: '',
    service_id: null,
    service_name: null,
    service_price: null,
    price_input: '',
    is_price_fixed: false,
    is_contractor_included_in_budget: false,
  };
}

interface ContractorServiceRowProps {
  item: ContractorEntryItem;
  onChange: (updated: ContractorEntryItem) => void;
  onRemove: () => void;
  debounceFlushKey?: string | number;
  hidePrices?: boolean;
}

function ContractorServiceRow({ item, onChange, onRemove, debounceFlushKey, hidePrices = false }: ContractorServiceRowProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const [contractorInput, setContractorInput] = useState(item.contractor_name);
  const [contractorDropdownOpen, setContractorDropdownOpen] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [contractorHighlightIndex, setContractorHighlightIndex] = useState(0);
  const [serviceHighlightIndex, setServiceHighlightIndex] = useState(0);
  const contractorContainerRef = useRef<HTMLDivElement>(null);
  const serviceContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  /** Последний выбранный в UI contractor_id — чтобы не применить услугу от устаревшего fetch */
  const latestContractorPickIdRef = useRef<string | null>(null);
  const selectContractorRef = useRef<(id: string, name: string) => Promise<void>>(async () => {});

  const MIN_SEARCH_LENGTH = 2;
  const debouncedSearch = useDebouncedValue(contractorInput.trim(), 300, debounceFlushKey);
  const contractorDropdownStyle = useDropdownPosition(contractorContainerRef, contractorDropdownOpen);

  const { data: searchResponse, isFetching: isContractorFetching } = useQuery({
    queryKey: ['legal-entities', debouncedSearch],
    queryFn: () => api.legalEntities.list(debouncedSearch, { limit: 25, offset: 0 }),
    enabled: contractorDropdownOpen && debouncedSearch.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const searchResults = searchResponse?.items ?? [];

  const { data: services = [] } = useQuery<ContractorService[]>({
    queryKey: ['legal_entity_contracts', item.contractor_id],
    queryFn: () => api.contractors.services(item.contractor_id),
    enabled: !!item.contractor_id,
    staleTime: 60_000,
  });

  useEffect(() => {
    setContractorInput(item.contractor_name);
  }, [item.contractor_name]);

  useEffect(() => {
    setContractorHighlightIndex(0);
  }, [searchResults.length]);

  useEffect(() => {
    setServiceHighlightIndex(0);
  }, [services.length]);

  useClickOutside(contractorContainerRef, () => setContractorDropdownOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });
  useClickOutside(serviceContainerRef, () => setServiceDropdownOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });

  const selectContractor = useCallback(
    async (id: string, name: string) => {
      latestContractorPickIdRef.current = id;
      const base: ContractorEntryItem = {
        ...item,
        project_contractor_id: null,
        contractor_id: id,
        contractor_name: name,
        service_id: null,
        service_name: null,
        service_price: null,
        price_input: '',
        is_price_fixed: false,
        is_contractor_included_in_budget: false,
      };
      onChange(base);
      setContractorInput(name);
      setContractorDropdownOpen(false);

      const servicesList = await queryClient.fetchQuery<ContractorService[]>({
        queryKey: ['legal_entity_contracts', id],
        queryFn: () => api.contractors.services(id),
        staleTime: 60_000,
      });
      if (latestContractorPickIdRef.current !== id) return;
      if (servicesList.length === 1) {
        const svc = servicesList[0];
        onChange({
          ...base,
          service_id: svc.id,
          service_name: svc.service,
          service_price: svc.service_price,
          price_input: '',
          is_price_fixed: false,
        });
      }
    },
    [item, onChange, queryClient]
  );
  selectContractorRef.current = selectContractor;

  const openCreateLegalEntity = useCallback(() => {
    const q = contractorInput.trim();
    setContractorDropdownOpen(false);
    openCatalogManage({
      tab: 'legal_entities',
      nameSeed: q,
      onLegalEntityCreated: (entity) => {
        void selectContractorRef.current(entity.id, entity.name);
      },
    });
  }, [contractorInput, openCatalogManage]);

  const handleContractorInput = (value: string) => {
    setContractorInput(value);
    if (value.trim().length > 0) {
      setContractorDropdownOpen(true);
    } else {
      setContractorDropdownOpen(false);
      onChange({
        ...item,
        project_contractor_id: null,
        contractor_id: '',
        contractor_name: '',
        service_id: null,
        service_name: null,
        service_price: null,
        price_input: '',
        is_price_fixed: false,
        is_contractor_included_in_budget: false,
      });
    }
  };

  const handleContractorKeyDown = (e: React.KeyboardEvent) => {
    if (!contractorDropdownOpen) {
      if (e.key === 'ArrowDown' && contractorInput.trim().length >= MIN_SEARCH_LENGTH) setContractorDropdownOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setContractorHighlightIndex((i) => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setContractorHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = searchResults[contractorHighlightIndex];
      if (opt) selectContractor(opt.id, opt.name);
    } else if (e.key === 'Escape') {
      setContractorDropdownOpen(false);
    }
  };

  const selectService = useCallback(
    (svc: ContractorService | null) => {
      if (svc) {
        onChange({
          ...item,
          project_contractor_id: null,
          service_id: svc.id,
          service_name: svc.service,
          service_price: svc.service_price,
          price_input: '',
          is_price_fixed: false,
        });
      } else {
        onChange({
          ...item,
          project_contractor_id: null,
          service_id: null,
          service_name: null,
          service_price: null,
          price_input: '',
          is_price_fixed: false,
        });
      }
      setServiceDropdownOpen(false);
    },
    [item, onChange]
  );

  const handleServiceKeyDown = (e: React.KeyboardEvent) => {
    if (!serviceDropdownOpen) {
      if (e.key === 'ArrowDown' && item.contractor_id) setServiceDropdownOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setServiceHighlightIndex((i) => Math.min(i + 1, services.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setServiceHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const svc = services[serviceHighlightIndex];
      if (svc) selectService(svc);
    } else if (e.key === 'Escape') {
      setServiceDropdownOpen(false);
    }
  };

  return (
    <div className="contractor-service-row">
      <div className="contractor-service-row__row contractor-service-row__row_contractor">
        <div ref={contractorContainerRef} className="contractor-service-row__field-wrap">
          <div className="contractor-service-row__field">
            <input
              className="contractor-service-row__input contractor-service-row__input_contractor"
              value={contractorInput}
              onChange={(e) => handleContractorInput(e.target.value)}
              onFocus={() => {
                if (contractorInput.trim().length > 0) setContractorDropdownOpen(true);
              }}
              onBlur={() => setContractorDropdownOpen(false)}
              onKeyDown={handleContractorKeyDown}
              placeholder="Студия или к/а"
              autoComplete="off"
            />
            <ComboClearButton onClick={onRemove} />
          </div>
          {contractorDropdownOpen && contractorInput.trim().length > 0 &&
            (contractorInput.trim().length < MIN_SEARCH_LENGTH ? (
              createPortal(
                <div className="contractor-service-row__hint-dropdown" data-combo-dropdown style={contractorDropdownStyle}>
                  <div className="contractor-service-row__hint">Введите минимум 2 символа для поиска</div>
                </div>,
                document.body
              )
            ) : searchResults.length === 0 && isContractorFetching ? (
              createPortal(
                <div className="contractor-service-row__hint-dropdown" data-combo-dropdown style={contractorDropdownStyle}>
                  <div className="contractor-service-row__hint">Загрузка…</div>
                </div>,
                document.body
              )
            ) : searchResults.length === 0 ? (
              createPortal(
                <div className="contractor-service-row__hint-dropdown" data-combo-dropdown style={contractorDropdownStyle}>
                  <ComboEmptyCreatePanel
                    actions={
                      canManageCatalog
                        ? [
                            {
                              id: '__create_legal_entity',
                              label: 'Создать юрлицо',
                              onSelect: openCreateLegalEntity,
                            },
                          ]
                        : []
                    }
                  />
                </div>,
                document.body
              )
            ) : (
              <DropdownList<LegalEntity>
                triggerRef={contractorContainerRef}
                open={contractorDropdownOpen}
                options={searchResults.map((o) => ({
                  value: o,
                  label: o.name,
                  key: o.id,
                }))}
                highlightIndex={contractorHighlightIndex}
                onSelect={(opt) => selectContractor(opt.id, opt.name)}
                onHighlightChange={setContractorHighlightIndex}
              />
            ))}
        </div>
        {!hidePrices ? (
        <button
          type="button"
          className={`form__btn form__btn_outlined btn_l contractor-service-row__budget-btn${item.is_contractor_included_in_budget ? ' contractor-service-row__budget-btn_active' : ''}`}
          aria-pressed={item.is_contractor_included_in_budget}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onChange({ ...item, is_contractor_included_in_budget: !item.is_contractor_included_in_budget });
          }}
        >
          Бюджет
        </button>
        ) : null}
      </div>

      <div className="contractor-service-row__row contractor-service-row__row_service-price">
        <div ref={serviceContainerRef} className="contractor-service-row__field-wrap">
          <div
            className={`contractor-service-row__field contractor-service-row__service-trigger ${!item.contractor_id ? 'contractor-service-row__service-trigger_disabled' : ''}`}
            onClick={() => item.contractor_id && setServiceDropdownOpen((o) => !o)}
            onBlur={() => setServiceDropdownOpen(false)}
            onKeyDown={handleServiceKeyDown}
            tabIndex={item.contractor_id ? 0 : -1}
            role="combobox"
            aria-expanded={serviceDropdownOpen}
            aria-haspopup="listbox"
          >
            <span
              className={`contractor-service-row__input contractor-service-row__service-value ${!item.service_name ? 'contractor-service-row__service-value_placeholder' : ''} ${item.service_id ? 'contractor-service-row__service-value_with-clear' : ''}`.trim()}
            >
              {item.service_name ?? 'Договор'}
            </span>
            {item.contractor_id && item.service_id ? (
              <ComboClearButton
                onClick={(e) => {
                  e.stopPropagation();
                  selectService(null);
                }}
              />
            ) : null}
          </div>
          {serviceDropdownOpen &&
            item.contractor_id && (
              <DropdownList<ContractorService | null>
                triggerRef={serviceContainerRef}
                open={serviceDropdownOpen}
                options={services.map((s) => ({ value: s as ContractorService, label: s.service, key: s.id }))}
                highlightIndex={serviceHighlightIndex}
                onSelect={selectService}
                onHighlightChange={setServiceHighlightIndex}
              />
            )}
        </div>
        {!hidePrices ? (
        <PriceFixCombo
          className="contractor-service-row__price-fix-combo"
          value={item.price_input}
          onValueChange={(price_input) => onChange({ ...item, price_input })}
          isFixed={item.is_price_fixed}
          onFixedChange={(is_price_fixed) => onChange({ ...item, is_price_fixed })}
          placeholder={item.service_price != null ? formatPriceInputDisplay(item.service_price) : '0'}
        />
        ) : null}
      </div>
    </div>
  );
}

interface ContractorServiceListProps {
  value: ContractorEntryItem[];
  onChange: (entries: ContractorEntryItem[]) => void;
  debounceFlushKey?: string | number;
  hidePrices?: boolean;
}

export function ContractorServiceList({ value, onChange, debounceFlushKey, hidePrices = false }: ContractorServiceListProps) {
  const updateRow = (idx: number, updated: ContractorEntryItem) => {
    const next = [...value];
    next[idx] = updated;
    onChange(next);
  };

  const removeRow = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    onChange([...value, makeEmptyEntry()]);
  };

  return (
    <div className="contractor-service-list">
      {value.map((item, idx) => (
        <ContractorServiceRow
          key={item.key}
          item={item}
          onChange={(updated) => updateRow(idx, updated)}
          onRemove={() => removeRow(idx)}
          debounceFlushKey={debounceFlushKey}
          hidePrices={hidePrices}
        />
      ))}
      <button type="button" className="contractor-service-list__add" onClick={addRow}>
        + Добавить контрагента
      </button>
    </div>
  );
}
